'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

/**
 * Announces client-side navigation and moves focus to the new page's content.
 *
 * Next's App Router swaps the page without touching focus, so a keyboard user
 * stayed parked on the nav link they activated and a screen-reader user was
 * told nothing at all — the next Tab continued through the navigation rather
 * than the content that just arrived. Browsers do this for free on a full page
 * load; a client-side router has to do it deliberately.
 *
 * Two things happen on each route change:
 *   1. The document title is announced through a polite live region, so the
 *      user hears where they landed.
 *   2. Focus moves to <main id="main-content" tabIndex={-1}>, so the next Tab
 *      starts in the content. Focus is NOT moved on first paint — a fresh load
 *      already starts at the top, and stealing focus there would fight the
 *      browser and skip the skip-link.
 */
export function RouteAnnouncer() {
  const pathname = usePathname();
  const announcerRef = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    // Let the new page commit before reading its title or moving focus;
    // otherwise both still describe the page being left.
    const timer = setTimeout(() => {
      const main = document.getElementById('main-content');
      if (main) {
        main.focus({ preventScroll: true });
      }
      if (announcerRef.current) {
        const heading = document.querySelector('h1');
        const label =
          heading?.textContent?.trim() ||
          document.title ||
          'New page';
        announcerRef.current.textContent = `${label} — page loaded`;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname]);

  return (
    <div
      ref={announcerRef}
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    />
  );
}
